import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SubscriptionLicenseModal } from '@/features/subscription';
import { useOrgAccess } from '@/features/business';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import type { AuthUser } from '@/shared/types/auth.type';

interface UserMenuProps {
  user: AuthUser;
  onLogout: () => void;
}

/** 메뉴 폭. 화면이 더 좁으면 양옆 여백만 남기고 줄인다. */
const MENU_WIDTH = 260;
/** 메뉴와 화면 끝 사이에 남길 여백. */
const VIEWPORT_GUTTER = 16;

/**
 * 메뉴를 오른쪽으로 얼마나 밀어야 화면 안에 들어오는지 계산한다.
 *
 * 메뉴는 아바타 오른쪽 끝에서 왼쪽으로 펼쳐진다. 모바일에서는 아바타가 화면 가운데쯤 있어서
 * 그대로 펼치면 왼쪽이 화면 밖으로 잘린다. 그래서 열 때마다 위치를 재서 잘리는 만큼 민다.
 */
function menuShiftOf(anchor: HTMLElement): number {
  const { right } = anchor.getBoundingClientRect();
  const width = Math.min(
    MENU_WIDTH,
    window.innerWidth - VIEWPORT_GUTTER - VIEWPORT_GUTTER,
  );
  return Math.max(0, VIEWPORT_GUTTER - (right - width));
}

export function UserMenu({ user, onLogout }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const [shift, setShift] = useState(0);
  const [licenseOpen, setLicenseOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // 열려 있는 동안 화면을 돌리거나 크기가 바뀌면 위치를 다시 잰다.
  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      if (rootRef.current) setShift(menuShiftOf(rootRef.current));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  const toggle = () => {
    if (!open && rootRef.current) setShift(menuShiftOf(rootRef.current));
    setOpen((v) => !v);
  };

  // 콘솔 진입 = 사업자 계정 OR 조직 멤버십 보유(초대받은 개인 포함).
  const { hasAccess: canAccessConsole } = useOrgAccess();

  // 외부 클릭 닫기
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // ESC 닫기
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const initials = getInitials(user.name ?? user.email);
  const displayName = user.name ?? user.email.split('@')[0];

  return (
    <div ref={rootRef} className="relative">
      {/* Trigger - 아바타 */}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        className="group flex items-center gap-2 rounded-full p-0.5 pr-2 transition-colors hover:bg-black/[0.04] dark:hover:bg-white/10"
      >
        <span className="relative shrink-0">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt=""
              aria-hidden
              className="h-8 w-8 rounded-full object-cover ring-1 ring-black/5"
            />
          ) : (
            <span
              className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-neutral-900 to-neutral-700 text-[12px] font-bold tracking-wide text-white ring-1 ring-black/5"
              aria-hidden
            >
              {initials}
            </span>
          )}
        </span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          className={`hidden text-neutral-950/40 transition-transform duration-200 sm:block ${
            open ? 'rotate-180' : ''
          }`}
        >
          <path
            d="M2 3.5L5 6.5L8 3.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>

      {/* Dropdown */}
      <div
        role="menu"
        style={{ right: -shift }}
        className={`absolute top-[calc(100%+8px)] w-[min(260px,calc(100vw-32px))] origin-top-right overflow-hidden rounded-2xl border border-black/[0.08] bg-white/95 shadow-[0_12px_40px_-12px_rgba(0,0,0,0.18)] backdrop-blur-xl transition-all duration-200 ${
          open
            ? 'pointer-events-auto translate-y-0 opacity-100 scale-100'
            : 'pointer-events-none -translate-y-1 opacity-0 scale-[0.98]'
        }`}
      >
        {/* 사용자 정보 헤더 */}
        <div className="flex items-center gap-3 border-b border-black/[0.06] px-4 py-3.5">
          <span
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-neutral-900 to-neutral-700 text-[13px] font-bold text-white ring-1 ring-black/5"
            aria-hidden
          >
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[14px] font-semibold text-neutral-950">
              {displayName}
            </div>
            <div className="truncate text-[12px] text-neutral-950/55">
              {user.email}
            </div>
          </div>
        </div>

        {/* 메뉴 아이템 */}
        <div className="p-1.5">
          <MenuItem
            to={PATHS.MYPAGE}
            label="마이페이지"
            onClick={() => setOpen(false)}
            icon={
              <path
                d="M8 8a3 3 0 100-6 3 3 0 000 6zM2 14c0-2.5 2.5-4.5 6-4.5s6 2 6 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                fill="none"
              />
            }
          />
          <MenuItem
            to={PATHS.COUPON}
            label="쿠폰 등록"
            onClick={() => setOpen(false)}
            icon={
              <>
                <path
                  d="M2 5.5A1 1 0 013 4.5h10a1 1 0 011 1V7a1.5 1.5 0 000 3v1.5a1 1 0 01-1 1H3a1 1 0 01-1-1V10a1.5 1.5 0 000-3V5.5z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                  fill="none"
                />
                <path
                  d="M9.5 5v6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray="0.5 2"
                />
              </>
            }
          />
          <MenuItem
            to={PATHS.QUOTES}
            label="견적함"
            onClick={() => setOpen(false)}
            icon={
              <>
                <path
                  d="M4 2.5h6l2.5 2.5V13a.5.5 0 01-.5.5H4a.5.5 0 01-.5-.5V3a.5.5 0 01.5-.5z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                  fill="none"
                />
                <path
                  d="M5.5 7.5h5M5.5 10h3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </>
            }
          />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setLicenseOpen(true);
            }}
            className="group/item flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[14px] font-medium text-neutral-950/70 transition-colors hover:bg-black/[0.04] dark:hover:bg-white/10 hover:text-neutral-950"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              className="text-neutral-950/50 transition-colors group-hover/item:text-neutral-950/80"
            >
              <path
                d="M2.5 4.5h11a1 1 0 011 1v5a1 1 0 01-1 1h-11a1 1 0 01-1-1v-5a1 1 0 011-1z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
                fill="none"
              />
              <path
                d="M2 7h12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span className="flex-1">구독·라이선스</span>
          </button>
          {canAccessConsole && (
            <MenuItem
              to={PATHS.BUSINESS}
              label="기업 콘솔"
              onClick={() => setOpen(false)}
              icon={
                <>
                  <path
                    d="M2.5 14V3.5A1 1 0 013.5 2.5h5A1 1 0 019.5 3.5V14"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <path
                    d="M9.5 6.5h3a1 1 0 011 1V14M1.5 14h13"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <path
                    d="M4.5 5h1.5M4.5 7.5h1.5M4.5 10h1.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </>
              }
            />
          )}
        </div>

        <div className="border-t border-black/[0.06] p-1.5">
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[14px] font-medium text-neutral-950/70 transition-colors hover:bg-black/[0.04] dark:hover:bg-white/10 hover:text-neutral-950"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" className="text-neutral-950/50">
              <path
                d="M10 11l3-3-3-3M13 8H6M9 2H3.5A1.5 1.5 0 002 3.5v9A1.5 1.5 0 003.5 14H9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
            로그아웃
          </button>
        </div>
      </div>

      <SubscriptionLicenseModal
        open={licenseOpen}
        onClose={() => setLicenseOpen(false)}
      />
    </div>
  );
}

// ───────────────────────────────────

interface MenuItemProps {
  to: string;
  label: string;
  onClick: () => void;
  icon: React.ReactNode;
}

function MenuItem({ to, label, onClick, icon }: MenuItemProps) {
  return <Link
    to={to}
    role="menuitem"
    onClick={onClick}
    className="group/item flex items-center gap-2.5 rounded-lg px-3 py-2 text-[14px] font-medium text-neutral-950/70 transition-colors hover:bg-black/[0.04] dark:hover:bg-white/10 hover:text-neutral-950"
  >
    <svg width="16" height="16" viewBox="0 0 16 16" className="text-neutral-950/50 transition-colors group-hover/item:text-neutral-950/80">
      {icon}
    </svg>
    <span className="flex-1">{label}</span>
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      className="-translate-x-1 text-neutral-950/30 opacity-0 transition-all group-hover/item:translate-x-0 group-hover/item:opacity-100"
    >
      <path
        d="M4 2.5L7.5 6L4 9.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  </Link>
}

// ───────────────────────────────────

const getInitials = (nameOrEmail: string): string => {
  const trimmed = nameOrEmail.trim();
  if (!trimmed) return '?';

  // 이메일인 경우 @ 앞부분 사용
  const base = trimmed.includes('@') ? trimmed.split('@')[0] : trimmed;

  // 한글: 첫 글자
  if (/[\u3131-\uD79D]/.test(base)) return base[0];

  // 영문: 단어별 첫 글자 최대 2개
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};
